import {
  NumberPrimitive,
  LengthPrimitive,
  PercentagePrimitive,
  ColorPrimitive,
  type VariableReference,
} from '@packages/systems/core/utils/StyleVariables';
import type {
  DesignBuilder,
  RuntimeBuilder,
  TransformBuilder,
  Condition,
  NativeIdPath,
  TimelineRoleDefinition,
} from '@packages/systems/ix3/core';
import type {BreakpointID} from '@packages/systems/style/types';
import type {Application, SPEObject} from '@splinetool/runtime';

type DeclaredExtensionKeys = {
  triggers:
    | 'click'
    | 'hover'
    | 'load'
    | 'focus'
    | 'blur'
    | 'scroll'
    | 'scroll-scrub'
    | 'scroll-actions'
    | 'scroll-direction'
    | 'custom'
    | 'change'
    | 'mouse-move'
    | 'navbar'
    | 'dropdown'
    | 'popover'
    | 'modal'
    | 'exit-intent'
    | 'duration';

  actions:
    | 'class'
    | 'style'
    | 'transform'
    | 'lottie'
    | 'spline'
    | 'variable'
    | 'rive'
    | 'animate-rive'
    | 'mouse-follow'
    | 'custom-property';

  conditions:
    | 'prefersReducedMotion'
    | 'webflowBreakpoints'
    | 'customMediaQuery'
    | 'colorScheme'
    | 'elementDataAttribute'
    | 'currentTime'
    | 'elementState';

  targetResolvers:
    | 'body'
    | 'id'
    | 'inst'
    | 'trigger-only'
    | 'trigger-only-parent'
    | 'class'
    | 'selector'
    | 'attribute'
    | 'any-element'
    | 'viewport'
    | 'popover-element'
    | 'popover-backdrop'
    | 'modal-element'
    | 'modal-backdrop';
};

export const enum IX3_WF_EXTENSION_KEYS {
  // Targets and Actions
  CLASS = 'wf:class', // and action

  // Targets
  BODY = 'wf:body',
  ID = 'wf:id',
  TRIGGER_ONLY = 'wf:trigger-only',
  TRIGGER_ONLY_PARENT = 'wf:trigger-only-parent',
  SELECTOR = 'wf:selector',
  ATTRIBUTE = 'wf:attribute',
  INST = 'wf:inst',
  ANY_ELEMENT = 'wf:any-element',
  VIEWPORT = 'wf:viewport',
  POPOVER_ELEMENT = 'wf:popover-element',
  POPOVER_BACKDROP = 'wf:popover-backdrop',
  MODAL_ELEMENT = 'wf:modal-element',
  MODAL_BACKDROP = 'wf:modal-backdrop',

  // Actions
  STYLE = 'wf:style',
  TRANSFORM = 'wf:transform',
  CUSTOM_PROPERTY = 'wf:custom-property',
  LOTTIE = 'wf:lottie',
  SPLINE = 'wf:spline',
  VARIABLE = 'wf:variable',
  RIVE = 'wf:rive',
  ANIMATE_RIVE = 'wf:animate-rive',
  MOUSE_FOLLOW = 'wf:mouse-follow',

  // Triggers
  CLICK = 'wf:click',
  HOVER = 'wf:hover',
  LOAD = 'wf:load',
  FOCUS = 'wf:focus',
  BLUR = 'wf:blur',
  SCROLL = 'wf:scroll',
  SCROLL_SCRUB = 'wf:scroll-scrub',
  SCROLL_ACTIONS = 'wf:scroll-actions',
  SCROLL_DIRECTION = 'wf:scroll-direction',
  CUSTOM = 'wf:custom',
  CHANGE = 'wf:change',
  MOUSE_MOVE = 'wf:mouse-move',
  NAVBAR = 'wf:navbar',
  DROPDOWN = 'wf:dropdown',
  POPOVER = 'wf:popover',
  MODAL = 'wf:modal',
  EXIT_INTENT = 'wf:exit-intent',
  DURATION = 'wf:duration',

  // Conditions
  PREFERS_REDUCED_MOTION = 'wf:prefersReducedMotion',
  WEBFLOW_BREAKPOINTS = 'wf:webflowBreakpoints',
  CUSTOM_MEDIA_QUERY = 'wf:customMediaQuery',
  COLOR_SCHEME = 'wf:colorScheme',
  ELEMENT_DATA_ATTRIBUTE = 'wf:elementDataAttribute',
  CURRENT_TIME = 'wf:currentTime',
  ELEMENT_STATE = 'wf:elementState',
}

export type WFDesignBuilder = DesignBuilder<DeclaredExtensionKeys>;

export type WFRuntimeBuilder = RuntimeBuilder<DeclaredExtensionKeys>;

export type WFTransformsBuilder<TContext extends Record<string, unknown>> =
  TransformBuilder<DeclaredExtensionKeys, TContext>;

// ============================================================================
// Action Types
// ============================================================================

export type LottieActionConfig = {
  /** Starting position as decimal (0-1) where 1 = 100%, or a variable reference */
  from: number | VariableReference;
  /** Ending position as decimal (0-1) where 1 = 100%, or a variable reference */
  to: number | VariableReference;
  /** Whether the assigned duration is auto-computed or set by the user */
  manualDuration: boolean;
};

/**
 * Generic from/to value pair for numeric properties
 * Used across Spline animations (position, rotation, scale, intensity, zoom, opacity)
 */
export type FromToNumber = {from?: number; to?: number};

/**
 * Generic from/to value pair for string properties
 * Used for color and state name animations
 */
export type FromToString = {from?: string; to?: string};

export interface SplineProperties {
  positionX?: FromToNumber;
  positionY?: FromToNumber;
  positionZ?: FromToNumber;
  rotationX?: FromToNumber;
  rotationY?: FromToNumber;
  rotationZ?: FromToNumber;
  scaleX?: FromToNumber;
  scaleY?: FromToNumber;
  scaleZ?: FromToNumber;
  intensity?: FromToNumber;
  opacity?: FromToNumber;
  zoom?: FromToNumber;
  color?: FromToString;
  stateName?: FromToString;
}
export type SplineActionConfig = {
  /** Single object UUID or comma-separated list of UUIDs for multi-object animation */
  objectId: string | undefined;
  animatingState?: boolean;
  spline: SplineProperties;
};

export type VariableAction = {
  variableId: string;
  /**
   * The target value for the variable animation.
   * `null` means the value has not been explicitly set — the variable's
   * current resolved value is shown as a dimmed placeholder in the editor
   * until the user edits it.
   */
  value:
    | NumberPrimitive
    | LengthPrimitive
    | PercentagePrimitive
    | ColorPrimitive
    | VariableReference
    | null;
};

export type VariableActionConfig = {
  variables: VariableAction[];
};

// ============================================================================
// Rive Shared Types
// ============================================================================

/**
 * Represents a single added property from a view model or state machine
 */
export type RiveAddedProperty = {
  /** Name of the property */
  propertyName: string;
  /** Type of the property (for rendering appropriate input) */
  propertyType:
    | 'string'
    | 'number'
    | 'boolean'
    | 'color'
    | 'enum'
    | 'trigger'
    | 'artboard';
  /** The value to set (may be a VariableReference when bound to a design variable) */
  value?: string | number | boolean | VariableReference | null;
};

/** The selected animation source (view model name and its instance name) */
export type RiveAnimationSource = {
  name: string;
  instanceName?: string;
} | null;

export type RiveProperties = {
  animationSource?: RiveAnimationSource;
  /** Properties that have been added to the action */
  addedProperties?: Record<string, RiveAddedProperty>;
};

export type RiveActionConfig = {
  rive: RiveProperties;
};

// ============================================================================
// Animate Rive Shared Types
// ============================================================================

/** Animatable property types — only number and color can be tweened over time */
export type AnimateRivePropertyType = 'number' | 'color';

/** A single property in the Animate Rive action (to value only; from is read from current VMI state) */
export type AnimateRiveAddedProperty = {
  /** Name of the property */
  propertyName: string;
  /** Type of the property */
  propertyType: AnimateRivePropertyType;
  /** Target value to animate to. null = not yet set by user. */
  value?: number | string | VariableReference | null;
};

export type AnimateRiveProperties = {
  animationSource?: RiveAnimationSource;
  /** Properties that have been added to the action */
  addedProperties?: Record<string, AnimateRiveAddedProperty>;
};

export type AnimateRiveActionConfig = {
  rive: AnimateRiveProperties;
};

// ============================================================================
// Spline Shared Types
// ============================================================================
export interface OrthographicCamera {
  zoom: number;
}

export interface PerspectiveCamera {
  fov: number;
}
export type SplineRawColor = {
  r: number;
  g: number;
  b: number;
  a?: number;
};

export interface SplineInstance {
  spline: Application & {
    getAllObjects: () => Array<
      SPEObject & {type: string; parentUuid?: string; data?: unknown}
    >;
    _scene: {
      entityByUuid: Record<
        string,
        SPEObject & {type: string; parentUuid?: string; data?: unknown}
      >;
      [key: string]: unknown;
    };
    _sharedAssetsManager: unknown;
    requestRender: () => void;
    findObjectById?: (
      id: string
    ) =>
      | (SPEObject & {type: string; parentUuid?: string; data?: unknown})
      | undefined;
  };
}

export interface OriginalSplineState {
  position: {x: number; y: number; z: number};
  rotation: {x: number; y: number; z: number};
  scale: {x: number; y: number; z: number};
  color?: string | undefined;
  intensity?: number;
  zoom?: number;
}

// ============================================================================
// Target Types
// ============================================================================

/**
 * CSS selector string for targeting elements
 */
export type SelectorValue = string | string[];

/**
 * Enumeration of scoping mechanisms for target selection
 */
export const enum TargetScope {
  /** Target all matching elements (default) */
  ALL = 'all',
  /** Target the parent of the current element */
  PARENT = 'parent',
  /** Target direct children of the current element */
  CHILDREN = 'children',
  /** Target elements that share the same parent as the current element */
  SIBLINGS = 'siblings',
  /** Target the next sibling element */
  NEXT = 'next',
  /** Target the previous sibling element */
  PREVIOUS = 'previous',
  /** Target the first ancestor matching a selector */
  FIRST_ANCESTOR = 'first-ancestor',
  /** Target the first descendant matching a selector */
  FIRST_DESCENDANT = 'first-descendant',
  /** Target all descendants matching a selector */
  DESCENDANTS = 'descendants',
  /** Target all ancestors matching a selector */
  ANCESTORS = 'ancestors',
}

/**
 * Selector scope that requires additional selector value
 */
export type SelectorScope = [
  type:
    | TargetScope.DESCENDANTS
    | TargetScope.ANCESTORS
    | TargetScope.FIRST_DESCENDANT
    | TargetScope.FIRST_ANCESTOR,
  value: string,
];

/**
 * Direct scope that doesn't require additional selector value
 */
export type DirectScope =
  | TargetScope.ALL
  | TargetScope.CHILDREN
  | TargetScope.SIBLINGS
  | TargetScope.PARENT
  | TargetScope.NEXT
  | TargetScope.PREVIOUS;

/**
 * Target value for CSS-based selectors (id, class, custom selector)
 */
export type TargetSelectorValue =
  | SelectorValue
  | [SelectorValue, SelectorScope | DirectScope];

/**
 * Target value for wf:inst (component instance) targets.
 * - Simple: [componentId, elementId]
 * - Scoped: [[componentId, elementId], scope]
 */
export type InstTargetValue =
  | NativeIdPath
  | [NativeIdPath, SelectorScope | DirectScope];

// ============================================================================
// Trigger Types
// ============================================================================

export type ClickTriggerConfig = {
  click?: 'each' | 'first' | 'second' | 'odd' | 'even' | 'custom';
  custom?: number;
};

export type HoverTriggerConfig = {
  type?: 'mouseover' | 'mouseenter' | 'mouseleave';
  hover?: 'each' | 'first' | 'second' | 'odd' | 'even' | 'custom';
  custom?: number;
  /**
   * Tri-state discriminator for the Hover model:
   * - `true`: two-group mode — binds mouseenter AND mouseleave and emits
   *   `{type: 'timeline-role', role}` callbacks (mouseEnter/mouseLeave) for
   *   role-based routing to two timelines.
   * - `false`: new single-group mode — binds mouseenter AND mouseleave and
   *   fires the trigger callback on both so the trigger's Control (e.g.
   *   `togglePlayReverse`) plays on enter and reverses on leave.
   * - `undefined`: legacy single-event mode driven by `type`/`hover` (the
   *   only state for data created before the multi-timeline Hover model).
   */
  multiTimeline?: boolean;
  /**
   * Which mouse events the new Hover model binds. Layers on top of
   * `multiTimeline` and only applies when `multiTimeline` is defined (the new
   * model); legacy data uses `type`/`hover` instead.
   * - `undefined`/`'both'`: bind mouseenter AND mouseleave (the default —
   *   single-group toggles play/reverse; two-group routes each event to its
   *   role-matched timeline).
   * - `'enter'`: bind mouseenter only (single-group plays on enter; two-group
   *   runs only the hover-in timeline).
   * - `'leave'`: bind mouseleave only (single-group plays on leave; two-group
   *   runs only the hover-out timeline).
   */
  eventMode?: 'both' | 'enter' | 'leave';
};

export type ScrollDirectionTriggerConfig = {
  /**
   * Seeded `false` at creation so `isNewModelGroupTrigger` recognizes the
   * trigger as group-capable and the "Add separate reverse actions" affordance
   * renders. Scroll direction has no legacy data to distinguish, so unlike
   * Hover this is never `undefined` on trigger-created data — but it stays
   * optional because the field is absent until the schema's `defaultValue`
   * seeds it.
   */
  multiTimeline?: boolean;
};

export type PageLoadTriggerConfig = {
  triggerPoint?: 'immediate' | 'DOMContentLoaded' | 'fullyLoaded';
};

export type FocusTriggerConfig = {
  useFocusWithin?: boolean;
};

export type CustomTriggerConfig = {
  eventName?: string;
};

// ============================================================================
// Multi-Timeline Trigger Types
// ============================================================================

/**
 * Configuration for mouse move triggers.
 * Each axis (X/Y) gets its own timeline that scrubs based on mouse position.
 *
 * Timelines are matched by role via triggerMetadata:
 * - 'mouseX': Timeline for X-axis movement
 * - 'mouseY': Timeline for Y-axis movement
 */
export type MouseMoveTriggerConfig = {
  /**
   * Smoothness/easing for the mouse movement in milliseconds.
   * Higher values create smoother, more delayed animation response.
   * @default 50
   */
  smoothness?: number;

  /**
   * Resting state position when mouse is idle or leaves the element.
   * Values are percentages (0-100) for each axis.
   * Both x and y are optional - if not set, defaults to 50 (center).
   * @default {x: 50, y: 50}
   */
  restingState?: {
    x?: number;
    y?: number;
  };
};

/**
 * Configuration for the Mouse Follow action.
 * Targets follow the mouse position with per-element smoothing (stagger simulation).
 * Axis is auto-set from the timeline role (mouseX → x, mouseY → y).
 */
export type MouseFollowActionConfig = {
  /**
   * Which axis this action controls. Optional: the runtime derives the
   * authoritative axis from the timeline role (mouseX → x, mouseY → y).
   * The store auto-assigns this for UX/display, but the field is never
   * load-bearing at runtime.
   */
  axis?: 'x' | 'y';
  /**
   * Behavior when the mouse leaves the trigger element.
   * - 'return' (default): tween elements back to their starting position.
   * - 'stay': elements settle at their last position.
   */
  leaveBehavior?: 'return' | 'stay';
  /**
   * Behavior the first time the cursor enters the trigger element (and
   * again on every re-enter).
   * - 'animate' (default): tween targets from their current value to the
   *   cursor position with a distance-scaled duration.
   * - 'snap': jump targets directly to the cursor's entry position, then
   *   begin normal chain-follow tracking.
   *
   * Reduced-motion users always snap regardless of this setting.
   */
  onEnter?: 'animate' | 'snap';
  /**
   * Anchor point of the target relative to the cursor. Stored as
   * either a CSS-style "X% Y%" string or a structured `ix3-origin`
   * object (same shape as Transform origin, allowing future variable
   * binding per-axis). When omitted, targets are centered on the
   * cursor ("50% 50%").
   */
  anchor?:
    | string
    | {
        type: 'ix3-origin';
        x: string | VariableReference;
        y: string | VariableReference;
      };
  /**
   * Logical Mouse Follow group shared by the X/Y projections. Designer uses
   * this to mirror edits and keep both projections in one undoable user
   * intent. Runtime ignores this field.
   */
  groupId?: string;
  /**
   * Legacy partner action pointer. New writes use `groupId`; this remains
   * readable so draft data created by earlier Mouse Follow builds can be
   * normalized instead of orphaning a projection.
   */
  syncedActionId?: string;
  /**
   * Whether the effect drives both axes ('full') or only one ('x-only' /
   * 'y-only'). Default is 'full': X and Y siblings exist and are kept in
   * sync. Setting 'x-only' or 'y-only' on a paired pair tells the editor's
   * exit reconciler to delete the redundant axis sibling on back-out.
   * Mirrored between paired actions so both sides agree on the mode at all
   * times.
   *
   * The runtime treats absence as 'full' for backward compatibility; data
   * that pre-dates this field continues to behave as before.
   */
  followMode?: 'full' | 'x-only' | 'y-only';
};

export type MouseFollowAxis = NonNullable<MouseFollowActionConfig['axis']>;

/**
 * Shape of a TimelineAction-like object with a `properties` map. Used by
 * `getMouseFollowConfig` so it can run across runtime and designer without
 * taking a dependency on the full schema type.
 */
type ActionWithProperties = {
  properties?: Record<string, unknown> | null;
};

/**
 * Reads the Mouse Follow config slice off an action's properties and narrows
 * it to `MouseFollowActionConfig`. Returns `undefined` when the slice is
 * absent or not a plain object. An empty object is valid all-default Mouse
 * Follow config. Shared between runtime (vanilla bundle) and designer —
 * do not add React/Flux imports here.
 */
export function getMouseFollowConfig(
  action: ActionWithProperties | null | undefined
): MouseFollowActionConfig | undefined {
  const raw = action?.properties?.[IX3_WF_EXTENSION_KEYS.MOUSE_FOLLOW];
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return undefined;
  }
  return raw as MouseFollowActionConfig;
}

/**
 * Returns the effective follow mode for a Mouse Follow action, defaulting to
 * 'full' when the field is absent. Used by both the runtime axis filter and
 * the designer editor display.
 */
export function getEffectiveFollowMode(
  config: MouseFollowActionConfig | null | undefined
): 'full' | 'x-only' | 'y-only' {
  return config?.followMode ?? 'full';
}

export function getSingleAxisMouseFollowMode(
  axis: MouseFollowAxis
): 'x-only' | 'y-only' {
  return axis === 'x' ? 'x-only' : 'y-only';
}

/**
 * Canonical "centered" anchor string used by both the editor (preset seed,
 * UI default-detection) and the runtime fallback. Avoids `'50% 50%'` string
 * literals scattered across packages drifting out of sync.
 */
export const DEFAULT_MOUSE_FOLLOW_ANCHOR = '50% 50%';

export const DEFAULT_MOUSE_MOVE_INTERVAL_DISTANCE = 100;

/**
 * Configuration for component-based triggers (Navbar, Dropdown).
 * These triggers respond to component open/close state changes.
 */
export type ComponentTriggerConfig = {
  /**
   * Which state change this trigger responds to.
   * - 'open': Fires when the component opens (animates to open state)
   * - 'close': Fires when the component closes (animates to closed state)
   */
  event: 'open' | 'close';
};

/**
 * Configuration for Navbar triggers.
 * Extends ComponentTriggerConfig since navbars have open/close states.
 */
export type NavbarTriggerConfig = ComponentTriggerConfig;

/**
 * Configuration for Dropdown triggers.
 * Extends ComponentTriggerConfig since dropdowns have open/close states.
 */
export type DropdownTriggerConfig = ComponentTriggerConfig;

/**
 * Configuration for the Popover trigger.
 *
 * The Popover element itself is the trigger target, so there is no selector:
 * `webflow-popover.js` dispatches the state change and the runtime handler
 * routes it to the matching timeline role.
 *
 * `event` is optional, unlike Navbar and Dropdown: one Popover trigger row
 * covers both directions and routes by the `state` the site module announces,
 * so the Designer never writes an `event` for it. The field stays available for
 * a code-authored interaction that wants to pin a row to one direction, which
 * `buildOverlay` honours.
 */
export type PopoverTriggerConfig = Omit<ComponentTriggerConfig, 'event'> &
  Pick<Partial<ComponentTriggerConfig>, 'event'>;

/**
 * Options every page-level one-shot trigger (Exit intent, Duration) shares.
 *
 * `firstWins`: the trigger fires only if no other one-shot on its interaction
 * already claimed the moment: fired and showed something. The Designer writes
 * it to all of them at once, from one checkbox shown on each.
 */
export type OneShotTriggerConfig = {
  firstWins?: boolean;
};

/**
 * Configuration for the Exit intent trigger. Fires when the pointer leaves the
 * viewport through the top edge.
 */
export type ExitIntentTriggerConfig = OneShotTriggerConfig;

/**
 * Configuration for the Duration trigger.
 * - `timeOnSite`: fires once, `duration` seconds after the page loads.
 * - `idle`: fires once the visitor has been inactive for `duration` seconds.
 *   Any pointer, key, scroll, or touch input restarts the countdown.
 */
export type DurationTriggerConfig = OneShotTriggerConfig & {
  mode?: DurationTriggerMode;
  /** Seconds. */
  duration?: number;
};

export const DURATION_TRIGGER_MODES = ['idle', 'timeOnSite'] as const;

export type DurationTriggerMode = (typeof DURATION_TRIGGER_MODES)[number];

export const DEFAULT_DURATION_TRIGGER_MODE: DurationTriggerMode = 'timeOnSite';

/** Seconds. Matches the default seeded into new Duration triggers. */
export const DEFAULT_DURATION_TRIGGER_SECONDS = 5;

/** Persist bounds for the Duration trigger's duration input (seconds). */
export const DURATION_TRIGGER_SECONDS_RANGE = {min: 0, max: 3600} as const;

/**
 * Smallest duration a mode can mean, which is not the same for both.
 *
 * `idle` counts down from the last input, so 0 fires on bind — before any
 * activity can reset it, which is the opposite of idle. `timeOnSite` measures
 * from page load, where firing immediately is a legitimate thing to ask for.
 *
 * Shared so the Designer field, the write invariant, and the runtime's
 * defensive clamp cannot drift: the panel already refused to persist 0 for
 * `idle`, and a code- or extension-authored `{mode: 'idle', duration: 0}` used
 * to walk straight past both of the others.
 */
export const minimumDurationSeconds = (mode: DurationTriggerMode): number =>
  mode === 'idle'
    ? Math.max(1, DURATION_TRIGGER_SECONDS_RANGE.min)
    : DURATION_TRIGGER_SECONDS_RANGE.min;

/**
 * CustomEvent the published-site Popover module dispatches on a popover element
 * as it opens and closes, so the `wf:popover` trigger can route to the matching
 * timeline role.
 *
 * `detail: {state: 'open' | 'close', done?: () => void}` — when `done` is
 * supplied the module is waiting on the animation before it finishes the
 * transition (a close holds the element in the top layer until then). The
 * literal is duplicated in `webflow-popover.js`, which is plain site JS and
 * does not import this package; keep the two in sync.
 */
export const IX3_POPOVER_STATE_EVENT = 'webflow:popover-state';

/**
 * The Modal counterpart of `IX3_POPOVER_STATE_EVENT`, dispatched on a modal's
 * `<dialog>` by `webflow-modal.js` with the same `{state, done?}` detail. A
 * held close keeps the dialog open, so the page stays locked and inert behind
 * it, until `done` runs. `webflow-modal.js` re-declares the literal.
 */
export const IX3_MODAL_STATE_EVENT = 'webflow:modal-state';

/**
 * Target keys whose stored value is a `[componentId, elementId]` path.
 *
 * Everything that remaps ids — cross-site copy/paste, component duplicate and
 * detach, snapshot import — has to walk exactly this set, and each of those used
 * to enumerate the keys itself. A Modal's own target joins by being added here.
 *
 * `wf:popover-backdrop` is included even though it stores nothing today: the
 * resolver still reads a path as a last resort, so a value that ever does arrive
 * must be remapped rather than left naming the source site's element.
 */
export const INST_PATH_TARGET_KEYS = [
  IX3_WF_EXTENSION_KEYS.INST,
  IX3_WF_EXTENSION_KEYS.POPOVER_ELEMENT,
  IX3_WF_EXTENSION_KEYS.POPOVER_BACKDROP,
  IX3_WF_EXTENSION_KEYS.MODAL_ELEMENT,
  IX3_WF_EXTENSION_KEYS.MODAL_BACKDROP,
] as const satisfies readonly IX3_WF_EXTENSION_KEYS[];

/** Whether a target key's stored value is a `[componentId, elementId]` path. */
export function isInstPathTargetKey(key: string): boolean {
  return (INST_PATH_TARGET_KEYS as readonly string[]).includes(key);
}

/**
 * Trigger keys whose OWN `wf:inst` target names one component instance.
 *
 * These are the component triggers that a `assignedInstances` narrowing on a
 * sibling trigger is measured against: a Click, Exit intent or Duration
 * added alongside them may drive a subset of the instances the
 * interaction names. Popover is the only entry today; Modal and Slider join by
 * being added here, and the trigger-level invariant, the store reducer, and the
 * runtime observer all follow without an edit of their own.
 *
 * Navbar and Dropdown are absent deliberately — both are `exclusivity:
 * 'standalone'`, so they can never have a sibling trigger to narrow.
 */
export const COMPONENT_INSTANCE_TRIGGER_KEYS = [
  IX3_WF_EXTENSION_KEYS.POPOVER,
  IX3_WF_EXTENSION_KEYS.MODAL,
] as const satisfies readonly IX3_WF_EXTENSION_KEYS[];

/**
 * The components the IX3 component manifest describes. A literal rather than
 * read from the manifest, so the published runtime can key its per-component
 * records by it without importing the manifest; the manifest's entries must
 * use these ids, and a runtime test checks every id has an entry.
 */
export type IX3ComponentId = 'popover' | 'modal';

/** Whether a trigger key is one whose own target names a component instance. */
export function isComponentInstanceTriggerKey(key: string): boolean {
  return (COMPONENT_INSTANCE_TRIGGER_KEYS as readonly string[]).includes(key);
}

/**
 * Marker the `wf:popover` trigger writes onto a popover element while it is
 * bound, telling `webflow-popover.js` that this popover has an open/close
 * animation and so needs the module to drive its transition rather than leaving
 * it to the native invoker.
 *
 * Re-exported rather than re-declared: the literal is owned by
 * `core-constants/Popover.ts`, beside the rest of the DOM contract, because
 * every bootflow rule that swaps the real backdrop element in for the native
 * `::backdrop` keys on it — so the LESS and the writer would otherwise share
 * nothing but a hand-copied string. `webflow-popover.js` does re-declare it (it
 * is plain site JS and imports no packages); a drift test keeps that aligned.
 */
export {POPOVER_ANIMATED_ATTRIBUTE} from '@packages/systems/core-constants';

/**
 * The Modal counterpart of `POPOVER_ANIMATED_ATTRIBUTE`: the `wf:modal` trigger
 * writes it onto a modal's `<dialog>` while bound, and `webflow-modal.js` only
 * holds closes for a dialog that carries it. Owned by `core-constants/Modal.ts`
 * for the same reason.
 */
export {MODAL_ANIMATED_ATTRIBUTE} from '@packages/systems/core-constants';

// ============================================================================
// Multi-Timeline Role Definitions
// ============================================================================

/**
 * Timeline role name constants for type safety and consistency.
 * Use these instead of magic strings when referencing timeline roles.
 */
export const TIMELINE_ROLE_NAMES = {
  MOUSE_X: 'mouseX',
  MOUSE_Y: 'mouseY',
  INTERVAL: 'interval',
  OPEN: 'open',
  CLOSE: 'close',
  MOUSE_ENTER: 'mouseEnter',
  MOUSE_LEAVE: 'mouseLeave',
} as const;

export type TimelineRoleName =
  (typeof TIMELINE_ROLE_NAMES)[keyof typeof TIMELINE_ROLE_NAMES];

export type MouseMoveTimelineContext =
  | {
      kind: 'mouse-x';
      role: typeof TIMELINE_ROLE_NAMES.MOUSE_X;
      axis: 'x';
      siblingRole: typeof TIMELINE_ROLE_NAMES.MOUSE_Y;
    }
  | {
      kind: 'mouse-y';
      role: typeof TIMELINE_ROLE_NAMES.MOUSE_Y;
      axis: 'y';
      siblingRole: typeof TIMELINE_ROLE_NAMES.MOUSE_X;
    }
  | {
      kind: 'interval';
      role: typeof TIMELINE_ROLE_NAMES.INTERVAL;
    }
  | {
      kind: 'other';
      role: string | undefined;
    };

export function getMouseMoveTimelineContext(
  role: string | null | undefined
): MouseMoveTimelineContext {
  if (role === TIMELINE_ROLE_NAMES.MOUSE_X) {
    return {
      kind: 'mouse-x',
      role,
      axis: 'x',
      siblingRole: TIMELINE_ROLE_NAMES.MOUSE_Y,
    };
  }
  if (role === TIMELINE_ROLE_NAMES.MOUSE_Y) {
    return {
      kind: 'mouse-y',
      role,
      axis: 'y',
      siblingRole: TIMELINE_ROLE_NAMES.MOUSE_X,
    };
  }
  if (role === TIMELINE_ROLE_NAMES.INTERVAL) {
    return {kind: 'interval', role};
  }
  return {kind: 'other', role: role ?? undefined};
}

/**
 * Standard timeline roles for Mouse Move triggers.
 */
export const MOUSE_MOVE_TIMELINE_ROLES = {
  MOUSE_X: {
    role: TIMELINE_ROLE_NAMES.MOUSE_X,
    label: 'Mouse X',
    usePercentCanvas: true,
  },
  MOUSE_Y: {
    role: TIMELINE_ROLE_NAMES.MOUSE_Y,
    label: 'Mouse Y',
    usePercentCanvas: true,
  },
  INTERVAL: {role: TIMELINE_ROLE_NAMES.INTERVAL, label: 'Interval'},
} as const satisfies Record<string, TimelineRoleDefinition>;

/**
 * Animation properties that support the mouseMove plugin's velocity
 * influence (their values scale by cursor speed when an interval timeline
 * fires). Exposed from `shared/` so both the Designer UI and the runtime
 * can agree on the capability list without duplicating the set.
 */
export const VELOCITY_CAPABLE_PROPS: ReadonlySet<string> = new Set([
  'x',
  'y',
  'scale',
  'scaleX',
  'scaleY',
  'rotation',
  'skewX',
  'skewY',
  'opacity',
]);

export function isVelocityInfluenceEnabled(
  action:
    | {
        pluginConfig?:
          | {type?: unknown; velocityInfluence?: unknown}
          | null
          | undefined;
      }
    | null
    | undefined
): boolean {
  return (
    action?.pluginConfig?.type === 'mouseMove' &&
    Boolean(action.pluginConfig.velocityInfluence)
  );
}

export function canUseVelocityInfluenceProperty(propertyId: string): boolean {
  return VELOCITY_CAPABLE_PROPS.has(propertyId);
}

export function isMouseMoveIntervalRole(role: string | undefined): boolean {
  return getMouseMoveTimelineContext(role).kind === 'interval';
}

/**
 * Maps a timeline role to the Mouse Follow axis it owns. Returns `null` for
 * roles that aren't mouseX/mouseY so callers don't silently default to `'x'`
 * for unrelated timelines (interval, navbar, etc.).
 */
export function mouseFollowRoleToAxis(
  role: string | undefined
): MouseFollowAxis | null {
  const context = getMouseMoveTimelineContext(role);
  return context.kind === 'mouse-x' || context.kind === 'mouse-y'
    ? context.axis
    : null;
}

export function getOppositeMouseFollowAxis(
  axis: MouseFollowAxis
): MouseFollowAxis {
  return axis === 'x' ? 'y' : 'x';
}

export function mouseFollowAxisToRole(
  axis: MouseFollowAxis
): typeof TIMELINE_ROLE_NAMES.MOUSE_X | typeof TIMELINE_ROLE_NAMES.MOUSE_Y {
  return axis === 'x'
    ? TIMELINE_ROLE_NAMES.MOUSE_X
    : TIMELINE_ROLE_NAMES.MOUSE_Y;
}

export function mouseFollowRoleToSiblingRole(
  role: string | undefined
):
  | typeof TIMELINE_ROLE_NAMES.MOUSE_X
  | typeof TIMELINE_ROLE_NAMES.MOUSE_Y
  | null {
  const context = getMouseMoveTimelineContext(role);
  return context.kind === 'mouse-x' || context.kind === 'mouse-y'
    ? context.siblingRole
    : null;
}

/**
 * Channel names used by the mouse-move plugin to dispatch raw pointer
 * position and trigger-leave notifications across the generic plugin event
 * bus on `AnimationCoordinator`. The mouse-follow action subscribes to
 * these; the mouse-move trigger publishes them. Core stays ignorant of any
 * specific channel name.
 *
 * Plugin-owned naming convention: `<plugin-key>:<channel>` so different
 * plugins can never collide on the bus.
 */
export const MOUSE_MOVE_CHANNELS = {
  POSITION: 'wf:mouse-move:position',
  LEAVE: 'wf:mouse-move:leave',
} as const;

/**
 * Payload shape published on `MOUSE_MOVE_CHANNELS.POSITION`. The action
 * narrows the bus's `unknown` data to this shape at the boundary.
 */
export type MouseMovePositionPayload = {
  x: number;
  y: number;
  triggerEl: HTMLElement;
  isViewport: boolean;
};

/**
 * Plugin payload supplied to `coordinator.fireInterval` by the mouse-move
 * trigger. The interval handler (`fireMouseMoveInterval`) narrows
 * `IntervalFireOptions.pluginPayload` to this shape.
 */
export type MouseMoveIntervalPayload = {
  cursorPos?: {x: number; y: number};
  velocityFactor?: number;
  dirX?: number;
  dirY?: number;
};

/**
 * Narrows an opaque `IntervalFireOptions.pluginPayload` value to the
 * mouse-move payload shape. Returns an empty object when the payload is
 * missing or the wrong shape so downstream code can keep its `?? defaults`
 * reads without a sea of nullish checks.
 *
 * Field-by-field validation rather than a blanket cast — a future required
 * field on `MouseMoveIntervalPayload` will surface here loudly instead of
 * silently undefined-deref-ing at the call site.
 */
export function narrowMouseMoveIntervalPayload(
  payload: unknown
): MouseMoveIntervalPayload {
  if (typeof payload !== 'object' || payload === null) return {};
  const raw = payload as Record<string, unknown>;
  const result: MouseMoveIntervalPayload = {};
  const cursorPos = raw.cursorPos;
  if (
    typeof cursorPos === 'object' &&
    cursorPos !== null &&
    typeof (cursorPos as {x: unknown}).x === 'number' &&
    typeof (cursorPos as {y: unknown}).y === 'number'
  ) {
    result.cursorPos = {
      x: (cursorPos as {x: number}).x,
      y: (cursorPos as {y: number}).y,
    };
  }
  if (typeof raw.velocityFactor === 'number') {
    result.velocityFactor = raw.velocityFactor;
  }
  if (typeof raw.dirX === 'number') result.dirX = raw.dirX;
  if (typeof raw.dirY === 'number') result.dirY = raw.dirY;
  return result;
}

/**
 * Standard timeline roles for component triggers (Navbar, Dropdown).
 * OPEN plays forward only. CLOSE auto-reuses OPEN and defaults to
 * `reverseFlipEase` so reverse playback feels adapted rather than mirrored;
 * when the user disables reuse the full forward+reverse control set applies.
 */
export const COMPONENT_TIMELINE_ROLES = {
  OPEN: {
    role: TIMELINE_ROLE_NAMES.OPEN,
    label: 'Open',
    allowedControls: ['play', 'restart'],
    defaultControl: 'play',
  },
  CLOSE: {
    role: TIMELINE_ROLE_NAMES.CLOSE,
    label: 'Close',
    allowedControls: ['play', 'restart', 'reverse', 'reverseFlipEase'],
    allowedControlsWhenReusing: ['reverse', 'reverseFlipEase'],
    defaultControl: 'play',
    defaultControlWhenReusing: 'reverseFlipEase',
    autoReusesRole: TIMELINE_ROLE_NAMES.OPEN,
  },
} satisfies Record<string, TimelineRoleDefinition>;

/**
 * Timeline roles for the native Popover state-change trigger.
 *
 * Deliberately separate from `COMPONENT_TIMELINE_ROLES`: Navbar and Dropdown
 * default Close to reusing Open in reverse, but a Popover's close is authored
 * independently (it fades both the popover and its backdrop to zero rather
 * than mirroring the staggered open). Omitting `autoReusesRole` is what makes
 * the Close timeline stand on its own.
 *
 * Labels read "Open actions" / "Close actions" to match the timeline-group
 * vocabulary the panel already uses elsewhere.
 *
 * Restart is the only control either role offers, which is what puts a popover
 * interaction on the same footing as any other multi-timeline one: each role is
 * authored in its own right and played forward from the top, so there is nothing
 * to choose. A single-option list is how a role says "no Control here" — the
 * action group's settings popover hides the dropdown rather than showing one
 * option, and the interaction's triggers are locked the same way multi-group
 * mode locks them.
 *
 * Restart rather than Play because every open is a fresh open. A `play` on a
 * timeline already parked at its end does nothing, and open and close do not
 * always run on the same GSAP timeline instance: a trigger bound to the backdrop
 * gets its own per-trigger-element sub-timeline (the popover targets resolve
 * relative to the firing trigger), so the popover's own open cannot rely on the
 * close having rewound it.
 */
export const POPOVER_TIMELINE_ROLES = {
  OPEN: {
    role: TIMELINE_ROLE_NAMES.OPEN,
    label: 'Open actions',
    allowedControls: ['restart'],
    defaultControl: 'restart',
  },
  CLOSE: {
    role: TIMELINE_ROLE_NAMES.CLOSE,
    label: 'Close actions',
    allowedControls: ['restart'],
    defaultControl: 'restart',
  },
} satisfies Record<string, TimelineRoleDefinition>;

/**
 * Timeline roles for the `wf:modal` trigger. The same object as Popover's: a
 * modal opens and closes the same way, so the roles, labels and Restart-only
 * control all carry over.
 */
export const MODAL_TIMELINE_ROLES = POPOVER_TIMELINE_ROLES;

/**
 * Standard timeline roles for the Hover trigger. Distinct from
 * `COMPONENT_TIMELINE_ROLES.OPEN`/`CLOSE` so hover events keep their
 * literal vocabulary (`mouseEnter`/`mouseLeave`) rather than overloading
 * the open/close metaphor used by Navbar and Dropdown.
 */
export const HOVER_TIMELINE_ROLES = {
  MOUSE_ENTER: {
    role: TIMELINE_ROLE_NAMES.MOUSE_ENTER,
    label: 'Hover in actions',
    allowedControls: ['play', 'restart'],
    defaultControl: 'play',
  },
  MOUSE_LEAVE: {
    role: TIMELINE_ROLE_NAMES.MOUSE_LEAVE,
    label: 'Hover out actions',
    allowedControls: ['play', 'restart', 'reverse', 'reverseFlipEase'],
    defaultControl: 'play',
  },
} satisfies Record<string, TimelineRoleDefinition>;

// ============================================================================
// Conditions Types
// ============================================================================

/**
 * Comparison operators for data attribute conditions
 */
export type ComparisonOperator =
  | '=' // equals
  | '~' // contains
  | '^' // startsWith
  | '$' // endsWith
  | '?' // exists
  | '>' // greaterThan
  | '<' // lessThan
  | '>=' // greaterThanOrEqual
  | '<='; // lessThanOrEqual

/**
 * Prefers Reduced Motion condition value type
 */
export interface PrefersReducedMotionValue {
  // No properties needed - presence indicates true
}

/**
 * Webflow Breakpoints condition value type
 */
export interface WebflowBreakpointsValue {
  /** Webflow breakpoints to match */
  breakpoints: BreakpointID[];
}

/**
 * Custom Media Query condition value type
 */
export interface CustomMediaQueryValue {
  /** Custom media query string */
  query: string;
}

/**
 * Color Scheme condition value type
 */
export interface ColorSchemeValue {
  /** Color scheme to match */
  scheme: 'light' | 'dark';
}

/**
 * Element Data Attribute condition value type
 */
export interface ElementDataAttributeValue {
  /** CSS selector to target elements */
  selector: string;
  /** Data attribute name (without 'data-' prefix) */
  attribute: string;
  /** Value to compare against */
  value?: string | number | boolean;
  /** Comparison operator */
  operator: ComparisonOperator;
}

/**
 * Current Time condition value type
 */
export interface CurrentTimeValue {
  /** Type of time comparison */
  comparison: 'before' | 'after' | 'between';
  /** Time in HH:MM format */
  time: string;
  /** End time for 'between' comparison in HH:MM format */
  endTime?: string;
}

/**
 * Element State condition value type
 */
export interface ElementStateValue {
  /** CSS selector to target elements */
  selector: string;
  /** State to check for */
  state: 'visible' | 'hidden' | 'hasClass';
  /** Class name to check (required when state is 'hasClass') */
  className?: string;
}

/**
 * Typed condition definitions for each condition type
 */
export type PrefersReducedMotionCondition =
  Condition<PrefersReducedMotionValue>;
export type WebflowBreakpointsCondition = Condition<WebflowBreakpointsValue>;
export type CustomMediaQueryCondition = Condition<CustomMediaQueryValue>;
export type ColorSchemeCondition = Condition<ColorSchemeValue>;
export type ElementDataAttributeCondition =
  Condition<ElementDataAttributeValue>;
export type CurrentTimeCondition = Condition<CurrentTimeValue>;
export type ElementStateCondition = Condition<ElementStateValue>;

/**
 * Union type of all available condition types
 */
export type AnyCondition =
  | PrefersReducedMotionCondition
  | WebflowBreakpointsCondition
  | CustomMediaQueryCondition
  | ColorSchemeCondition
  | ElementDataAttributeCondition
  | CurrentTimeCondition
  | ElementStateCondition;
